import { SearchPart } from "@components/Pages/Machines/Search/Search.props";
import {
  MTFiltersValuesI,
  MTSort,
  SortDirection,
} from "@services/getMinerList";
import { MinerTabType } from "@services/http/responses/BaseResponse";
import { IMinerProfile } from "@store/minerProfiles/reducer";
import { fromEntries } from "@utils/entries";

export enum ActionTypesMinersSelect {
  SELECT = "MINERS_SELECT_SELECT",
  ADD = "MINERS_SELECT_ADD",
  REMOVE = "MINERS_SELECT_REMOVE",
  SELECT_ALL = "MINERS_SELECT_SELECT_ALL",
  REMOVE_ALL = "MINERS_SELECT_REMOVE_ALL",
  SET_SHOWN = "MINERS_SELECT_SET_SHOWN_MINERS",
  REORDER_SHOWN = "MINERS_SELECT_REORDER_SHOWN_MINERS",
  ADD_SHOWN = "MINERS_SELECT_ADD_SHOWN_MINERS",
  EXCLUDE = "MINERS_SELECT_EXCLUDE",
  INCLUDE = "MINERS_SELECT_INCLUDE",
  SET_REQUEST_PARAMS = "MINERS_SELECT_SET_REQUEST_PARAMS",
  SCROLL_TO = "MINERS_SELECT_SCROLL_TO",
  RESET_SCROLL = "MINERS_SELECT_RESET_SCROLL",
  SET_DRAG = "MINERS_SELECT_SET_DRAG",
  SET_TAB = "MINERS_SELECT_SET_TAB",
  SET_SUBTAB_NUM = "MINERS_SELECT_SET_SUBTAB_NUM",
  SET_SUBTABS = "MINERS_SELECT_SET_SUBTABS",
  NEED_DESELECT = "MINERS_SELECT_NEED_DESELECT_ALL",
  INIT_USER_TABS = "MINERS_SELECT_INIT_USER_TABS",
  SET_DRAG_OVER_TAB = "MINERS_SET_DRAG_OVER_TAB",
}

// all|active|warn|broken|problem|offline racks|store|retired
export enum Tab {
  SERVICE = -1,     // сервис
  INCORRECTID = -2, // неверный ID
  ACTIVE = -3,      // активные
  UNKNOWN = -4,     // отдельный API
  ALL = -5,         // все
  PROBLEM = -6,     // проблемные
  OFFLINE = -7,     // не активные
  RACKS = -8,       // на полке
  STORE = -9,       // склад
  RETIRED = -10,    // выбывшие
};

const tabIDToTypeTable: Record<Tab, MinerTabType> = {
  [Tab.SERVICE]: "broken",   // сервис
  [Tab.INCORRECTID]: "warn",     // неверный ID
  [Tab.ACTIVE]: "active",   // активные
  [Tab.UNKNOWN]: "unknown",  // отдельный API
  [Tab.ALL]: "all",      // все
  [Tab.PROBLEM]: "problem",  // проблемные
  [Tab.OFFLINE]: "offline",  // не активные
  [Tab.RACKS]: "racks",    // на полке
  [Tab.STORE]: "storage",  // склад
  [Tab.RETIRED]: "excluded", // выбывшие
} as const;

export type TabIDToType = (id: Tab | number) => MinerTabType | undefined;

export const tabIDToType: TabIDToType = (id: Tab | number): MinerTabType | undefined =>
  id < 0
    ? tabIDToTypeTable[id as Tab]
    : id > 0
      ? id
      : undefined;

export interface SelectedMinerI {
  id: string;
  miner_id: string;
  profile?: IMinerProfile;
  page: [number, number];
  type?: string;
  isOwnMining: boolean;
}

export const selectedMinerProfileName = (m: SelectedMinerI) => m.profile ? m.profile.name : m.type ? m.type : "";

export interface SelectedQueryResultI {
  miners: SelectedMinerI[];
  total: number;
}

interface SelectedSomeMinersForMarshalI {
  selected: SelectedMinerI[];
}

interface SelectedManyMinersForMarshalI {
  excluded: string[];
  total: number;
}

export type SelectedMinersForMarshalI =
  | SelectedSomeMinersForMarshalI
  | SelectedManyMinersForMarshalI;

const manyMinersSelectedMarshalled = (
  m: SelectedMinersForMarshalI
): m is SelectedManyMinersForMarshalI =>
  (m as SelectedManyMinersForMarshalI).excluded !== undefined;
const someMinersSelectedMarshalled = (
  m: SelectedMinersForMarshalI
): m is SelectedSomeMinersForMarshalI =>
  (m as SelectedSomeMinersForMarshalI).selected !== undefined;

export interface RequestParamsI {
  filter?: MTFiltersValuesI;
  sort?: MTSort;
  dir?: SortDirection;
}

export type SelectionQuery = (
  offset: number,
  limit: number,
  filter?: MTFiltersValuesI,
  sort?: MTSort,
  dir?: SortDirection
) => Promise<SelectedQueryResultI | void>;

export interface SelectedMinersSetI {
  [key: string]: SelectedMinerI;
}

export interface MinersIDSetI {
  [key: string]: number;
}

export interface MinersSelectionI {
  selected: SelectedMinerI[];
  excluded?: MinersIDSetI;
  query?: SelectionQuery;
  total?: number;
}

export const manyMinersSelected = (miners: MinersSelectionI): boolean =>
  !!miners.query;
export const allMinersSelected = (miners: MinersSelectionI): boolean =>
  !!miners.query && !miners.excluded?.length;
export const noMinersSelected = (miners: MinersSelectionI): boolean =>
  !miners.selected.length && !miners.query;
export const hasMinersSelected = (miners: MinersSelectionI): boolean =>
  !!miners.query || !!miners.selected.length;
export const isMinerSelected = (miners: MinersSelectionI, id: string) => {
  const result = manyMinersSelected(miners)
    ? !miners.excluded?.hasOwnProperty(id)
    : miners.selected.findIndex((m) => `${m.id}` === `${id}`) >= 0;
  return result;
};

export const totalMinersSelected = (miners: MinersSelectionI): number =>
  manyMinersSelected(miners)
    ? (miners.total || 0) - Object.keys(miners.excluded || {}).length
    : miners.selected.length;

export const marshalSelectedMiners = (
  miners: MinersSelectionI
): SelectedMinersForMarshalI =>
  manyMinersSelected(miners)
    ? ({
      excluded: Object.keys(miners.excluded || {}),
    } as SelectedManyMinersForMarshalI)
    : ({ selected: miners.selected } as SelectedSomeMinersForMarshalI);

export const selectedMinersString = (miners: MinersSelectionI): string =>
  manyMinersSelected(miners)
    ? `!${Object.keys(miners.excluded || {}).join(",")}`
    : miners.selected.map((m) => m.id).join(",");

type ServerSideFixAnswer = SelectedMinerI[];

export type ServerSideFixMinersList = (
  ids: string[]
) => Promise<ServerSideFixAnswer | void>;

export const idsArrayToIDSet = (ids: string[]): MinersIDSetI => {
  const idSet: MinersIDSetI = {};
  for (const id of ids) idSet[id] = 1;
  return idSet;
};

export const selectedMinersArrayToIDSet = (
  miners: SelectedMinerI[]
): MinersIDSetI => {
  const idSet: MinersIDSetI = {};
  for (const m of miners) idSet[m.id] = 1;
  return idSet;
};

export const selectedMinersArrayToMinersSet = (
  miners: SelectedMinerI[]
): SelectedMinersSetI => {
  const minersSet: SelectedMinersSetI = {};
  for (const m of miners) minersSet[m.id] = m;
  return minersSet;
};

export const selectedMinersSetToSortedArray = (
  miners: SelectedMinersSetI
): SelectedMinerI[] => {
  return Object.values(miners).sort((a, b) => a.id.localeCompare(b.id));
};

export const unmarshalSelectedMiners = async (
  miners: SelectedMinersForMarshalI,
  fix: ServerSideFixMinersList
): Promise<[MinersSelectionI, boolean] | void> => {
  if (manyMinersSelectedMarshalled(miners)) {
    let excluded = miners.excluded;
    if (excluded.length > 0) {
      const e = await fix(miners.excluded);
      if (!e) return;
      excluded = e.map((m) => m.id);
    }
    return [
      {
        selected: [],
        excluded: idsArrayToIDSet(excluded),
        total: miners.total,
      },
      true,
    ];
  }

  let fixed: SelectedMinerI[] = [];
  if (miners.selected.length > 0) {
    const f = await fix(miners.selected.map((m) => m.id));
    if (!f) return;
    fixed = f;
  }

  const tab = selectedMinersArrayToMinersSet(fixed);
  const result: MinersSelectionI = { selected: [] };
  for (const m of miners.selected) {
    if (tab.hasOwnProperty(m.id)) {
      result.selected.push({ ...tab[m.id], page: m.page });
    }
  }
  return [result, false];
};

export const isErrorUnmarshalledSelectedMiners = (
  a: MinersSelectionI | Error
): a is Error => (a as Error).message !== undefined;

export interface ActionBaseMinersSelect {
  type: ActionTypesMinersSelect;
  payload?: any;
}

export interface ActionSelect extends ActionBaseMinersSelect {
  payload: MinersSelectionI;
}

export interface ActionAdd extends ActionBaseMinersSelect {
  payload: SelectedMinerI[];
}

export interface ActionRemove extends ActionBaseMinersSelect {
  payload: string[];
}

export interface ActionSelectAll extends ActionBaseMinersSelect {
  payload: {
    page?: SelectedMinerI[];
    query: SelectionQuery;
    total: number;
  };
}

export interface ActionRemoveAll extends ActionBaseMinersSelect { }

export interface ActionSetDrag extends ActionBaseMinersSelect {
  payload: {
    ids?: string[];
    fromTab: number;
  };
}

export interface ActionSetTab extends ActionBaseMinersSelect {
  payload: number;
}

export interface ActionSetSubTabNum extends ActionBaseMinersSelect {
  payload: number;
}

export interface ActionSetSubTabs extends ActionBaseMinersSelect {
  payload: SearchPart[];
}

export interface ActionSetShownMiners extends ActionBaseMinersSelect {
  payload: {
    miners: SelectedMinerI[];
    total: number;
    received: number;
  };
}

export interface ActionAddShownMiners extends ActionBaseMinersSelect {
  payload: {
    miners: SelectedMinerI[];
    total: number;
    received: number;
  };
}

export interface ActionReorderShownMiners extends ActionBaseMinersSelect {
  payload: SelectedMinerI[];
}

export interface ActionExcludeMinersFromAllSelected
  extends ActionBaseMinersSelect {
  payload: string[];
}

export interface ActionIncludeMinersToAllSelected
  extends ActionBaseMinersSelect {
  payload: string[];
}

export interface ActionSetRequestParams extends ActionBaseMinersSelect {
  payload: RequestParamsI;
}

export interface ActionScrollTo extends ActionBaseMinersSelect {
  payload: number; // miner id
}

export interface ActionResetScroll extends ActionBaseMinersSelect { }
export interface ActionNeedDeselect extends ActionBaseMinersSelect {
  payload: boolean;
}

export interface ActionSetDragOverTab extends ActionBaseMinersSelect {
  payload?: Tab;
}

export enum DragType {
  MOVE, // нужно сначала вызвать удаление, потом копирование
  COPY, // нужно вызвать только копирование
  MOVECALL, // однократный вызов API приводит сразу к переносу или копированию майнера, пользовательский выбор не нужен
  USER, // нужен пользовательский выбор
}

type DragSources = Partial<Record<Tab, DragType>>;

export interface TabDescriptor {
  name: string;
  id: number;
  hasItems?: boolean;
  title?: string;
  adminOnly?: boolean;
  dragSource?: boolean;
  dragTarget?: DragSources;
}

export const DefaultDragSources: DragSources = {
  [Tab.ALL]: DragType.COPY,
  [Tab.ACTIVE]: DragType.COPY,
  [Tab.INCORRECTID]: DragType.COPY,
  [Tab.OFFLINE]: DragType.COPY,
  [Tab.PROBLEM]: DragType.COPY,
  [Tab.RACKS]: DragType.COPY,
  [Tab.SERVICE]: DragType.USER,
  [Tab.STORE]: DragType.USER,
  [Tab.RETIRED]: DragType.USER,
} as const;

export const SpecialTabs: TabDescriptor[] = [
  {
    id: Tab.ALL,
    name: "machines.table.tabs.all_tab",
    title: "machines.table.tabs.all_tab.title",
    dragSource: true,
  }, //
  {
    id: Tab.RACKS,
    name: "machines.table.tabs.racks_tab",
    title: "machines.table.tabs.racks_tab.title",
    adminOnly: true,
    dragSource: true,
  }, //
  {
    id: Tab.ACTIVE,
    name: "machines.table.tabs.active_tab",
    title: "machines.table.tabs.active_tab.title",
    dragSource: true,
  },
  {
    id: Tab.PROBLEM,
    name: "machines.table.tabs.problem_tab",
    title: "machines.table.tabs.problem_tab.title",
    dragSource: true,
  }, //
  {
    id: Tab.OFFLINE,
    name: "machines.table.tabs.offline_tab",
    title: "machines.table.tabs.offline_tab.title",
    dragSource: true,
  }, //
  {
    id: Tab.SERVICE,
    name: "machines.table.tabs.service_tab",
    title: "machines.table.tabs.service_tab.title",
    dragSource: true,
    dragTarget: DefaultDragSources,
  }, //
  {
    id: Tab.STORE,
    name: "machines.table.tabs.store_tab",
    title: "machines.table.tabs.store_tab.title",
    dragSource: true,
    dragTarget: DefaultDragSources,
  }, //
  {
    id: Tab.RETIRED,
    name: "machines.table.tabs.retired_tab",
    title: "machines.table.tabs.retired_tab.title",
    adminOnly: true,
    dragSource: true,
    dragTarget: DefaultDragSources,
  }, //
  {
    id: Tab.INCORRECTID,
    name: "machines.table.tabs.incorrectid_tab",
    title: "machines.table.tabs.incorrectid_tab.title",
    adminOnly: true,
    dragSource: true,
  },
  {
    id: Tab.UNKNOWN,
    name: "machines.table.tabs.unknown_tab",
    title: "machines.table.tabs.unknown_tab",
    adminOnly: true,
  },
];

export const DragSources: ReadonlySet<Tab> = new Set(
  SpecialTabs.filter((t) => t.dragSource).map((t) => t.id)
);
export const DragTargets: Record<Tab, DragSources> = fromEntries<
  Tab,
  DragSources
>(SpecialTabs.filter((t) => t.dragTarget).map((t) => [t.id, t.dragTarget!]));
